import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useExtendBundle } from "../hooks/use-extend-bundle";
import { addDays, format } from "date-fns";

interface ExtendBundleDialogProps {
  bundleId: string;
  userId: string;
  endDate: string;
  trigger: React.ReactNode;
}

export function ExtendBundleDialog({
  bundleId,
  userId,
  trigger,
  endDate,
}: ExtendBundleDialogProps) {
  const [days, setDays] = useState(1);
  const [open, setOpen] = useState(false);
  const { mutate: extendBundle, isPending } = useExtendBundle();
  const { toast } = useToast();
  const newEndDate = addDays(new Date(endDate), days).toISOString();

  const handleExtend = () => {
    if (days < 1) {
      toast({
        title: "Error",
        description: "Please enter a valid number of days",
        variant: "destructive",
      });
      return;
    }

    extendBundle({ usage_id: bundleId, end_date: newEndDate, userId });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend bundle expiry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="days" className="text-sm font-medium">
              Number of days to extend
            </label>
            <Input
              id="days"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              placeholder="Enter number of days"
            />
            <p className="text-sm text-muted-foreground">
              Current expiry date: {format(endDate, "PPP")}
            </p>
            <p className="text-sm text-muted-foreground">
              New expiry date:{" "}
              <span className="underline">{format(newEndDate, "PPP")}</span>
            </p>
          </div>
          <Button
            onClick={handleExtend}
            disabled={isPending}
            className="w-full"
          >
            {isPending ? "Extending..." : "Extend Bundle"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
